<script setup lang="ts">
import { BeamConstancyWorkStepDto } from '@/api/generator';
import { useFormMode } from '@/components/forms/useFormMode';
import EvaluationState from '@/components/evaluation/EvaluationState.vue';
import AnalysisResults from '@/components/measurement/AnalysisResults.vue';
import Tooltip from '@/components/Tooltip/Tooltip.vue';
import { mdiEyeOff, mdiEye, mdiPlus } from '@mdi/js';
import { fullName } from '@/utils/helper';
import { useJobOccurrence } from '../../utils/useJobOccurrence';
import { computed } from 'vue';
import TimeStamp from '@/components/EntityHistory/components/TimeStamp.vue';
import Button from '@/components/buttons/Button.vue';

const props = defineProps<{
  step: BeamConstancyWorkStepDto;
}>();

const mode = useFormMode();

const { toggleIgnoreResult, reload } = useJobOccurrence();

const hasMeasurements = computed(() => props.step.results?.length);
</script>

<template>
  <v-row>
    <v-col>
      <h5>{{ $t('Measurement_one') }}</h5>

      <p v-if="!hasMeasurements">{{ $t('NoMeasurements') }}</p>

      <v-expansion-panels v-else>
        <v-expansion-panel v-for="result in step.results" :key="result.id">
          <template #title>
            <div class="d-flex align-center w-100 mr-3">
              <v-icon :icon="mdiPlus" class="mr-2" />

              <div class="me-auto">
                <TimeStamp
                  :time="result.creationTime"
                  :user="fullName(result.creatorUser ?? undefined)"
                  :singleLine="true"
                />
              </div>

              <div class="d-flex align-center">
                <EvaluationState
                  :value="result.output || undefined"
                  :disabled="result.state === 'Ignored'"
                />
              </div>
            </div>
          </template>

          <template #text>
            <div class="d-flex align-center justify-end">
              <Tooltip>
                <template #tooltip>
                  {{ result.state === 'Ignored' ? $t('Ignored') : $t('Acknowledged') }}
                </template>
                <template #target="{ props: iconBtnProps }">
                  <Button
                    v-if="mode === 'execution'"
                    :icon="result.state === 'Ignored' ? mdiEyeOff : mdiEye"
                    variant="text"
                    :aria-label="result.state === 'Ignored' ? $t('Ignored') : $t('Acknowledged')"
                    v-bind="iconBtnProps"
                    @click.stop="toggleIgnoreResult(result)"
                  />
                </template>
              </Tooltip>
            </div>

            <v-row>
              <v-col>
                <AnalysisResults
                  :value="result.evaluationResults || []"
                  :evaluations="step.evaluations ?? undefined"
                  :disabled="result.state === 'Ignored'"
                  @change="reload()"
                />
              </v-col>
            </v-row>
          </template>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-col>
  </v-row>
</template>
